import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Bell } from "lucide-react";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import SignInPrompt from "@/pages/member/SignInPrompt";
import { renderCopy, renderHref } from "@/pages/member/NotificationsPanel";

export default function NotificationsPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    client.get("/notifications", { params: { limit: 200 } })
      .then((r) => setItems(r.data.notifications || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    // Wave C bug O (Vania 7 Oct) · the "mark all read" fire on mount stays
    // (opening the full inbox is an explicit "I saw everything" action).
    client.post("/notifications/mark-all-read").catch(() => {});
  }, [user, load]);

  // Wave C bug O (Vania 7 Oct) · a tapped row persists per-id read.
  const onRowClick = async (id) => {
    try { await client.post(`/notifications/${id}/read`); } catch {}
  };

  if (!authLoading && !user) {
    return <SignInPrompt title="notifications"
                         line="sign in to see what you missed."
                         next="/app/notifications" testId="notifications-signin" />;
  }

  return (
    <div className="flex h-full flex-col" data-testid="notifications-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="notifications · watu wako" />
      <header className="flex items-center gap-3 px-4 py-3"
              style={{ borderBottom: "1px solid var(--ww-line)" }}>
        <button onClick={() => nav(-1)} aria-label="back"
                data-testid="notifications-back"
                style={{ background: "transparent", border: 0, color: "var(--ww-text)",
                         cursor: "pointer", display: "flex", alignItems: "center" }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 16,
                       textTransform: "lowercase" }}>notifications</div>
      </header>
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10"
           style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 640 }}>
          {items === null && (
            <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>
          )}
          {items && items.length === 0 && (
            <div className="rounded-2xl border p-8 text-center" data-testid="notifications-empty"
                 style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
              <span style={{ width: 56, height: 56, borderRadius: 999,
                             background: "var(--ww-ground)",
                             border: "1px solid var(--ww-line)",
                             display: "inline-grid", placeItems: "center",
                             color: "var(--ww-text-muted)" }}>
                <Bell size={22} />
              </span>
              <p className="mt-3" style={{ fontFamily: "Arial", fontSize: 14,
                                             color: "var(--ww-text-muted)" }}>
                nothing new yet. keep saving events · activity shows up here.
              </p>
            </div>
          )}
          {items && items.length > 0 && (
            <ul data-testid="notifications-full-list">
              {items.map((n) => (
                <li key={n.id} data-testid={`notif-page-row-${n.id}`}
                    data-read={n.read_at ? "1" : "0"}
                    style={{ borderBottom: "1px solid var(--ww-line)" }}>
                  <Link to={renderHref(n)} onClick={() => onRowClick(n.id)}
                        style={{ display: "flex", alignItems: "center", gap: 12,
                                 padding: "14px 4px", color: "var(--ww-text)",
                                 textDecoration: "none" }}>
                    <span style={{ width: 40, height: 40, borderRadius: 999,
                                   background: "var(--ww-sunken)",
                                   border: "1px solid var(--ww-line)",
                                   display: "grid", placeItems: "center",
                                   color: "var(--ww-text-muted)" }}>
                      <Bell size={16} />
                    </span>
                    <div style={{ flex: 1, fontFamily: "Arial", fontSize: 14 }}>
                      {renderCopy(n)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
